<script setup lang="ts">
import { type PspMethodUnion, type PspEnum, type TransactionStateUnion, PspMethodEnum } from '@payments/types'
import { BroadcastMessageType } from '@core/types'
import type { LocationQueryRaw } from 'vue-router'
import { loadedOnPaymentsPage } from '@payments/utils/loadedOnPaymentsPage'
import { ref, computed, onMounted, onBeforeUnmount, whenever, definePageMeta, useTimeoutFn, useRoute, useUserInfo, useDeposit, usePayments, useHead, useState, navigateTo, usePaymentsConfig, useCoreConfig, useThemeLogo, useI18n, useWithdraw, useSharedBroadcast, useGame, useTracking, OpenapiError, markDepositReturnFullLoad } from '#imports'

const route = useRoute()
const { isLoggedIn } = useUserInfo()
const { trackEvent } = useTracking()
const { t } = useI18n()
const { pendingPageRedirectUrl } = usePaymentsConfig()
const { site } = useCoreConfig()
const logo = useThemeLogo('menu')
const { pspMethod, isReturning, isTransactionSuccessful, isTransactionFailed, isTransactionInconsistent } = usePayments()
const { waitForTransactionStatus, isFirstDeposit: isUsersFirstDeposit, resetPayment } = useDeposit()
const { useReceipt } = useWithdraw()
const { sendMessage } = useSharedBroadcast()
const { getGameUrl, getGameParams } = useGame()

const { start: startOpenLoopTimeout } = useTimeoutFn(() => {
    fetchWithdrawReceipt.value = true
}, 5_000, { immediate: pspMethod.value === PspMethodEnum.WITHDRAW })

definePageMeta({
    title: 'Deposit pending',
    layout: false,
    hideOverlayContainer: true,
    hideIntercom: true,
})

useHead({
    meta: [
        { name: 'robots', content: 'noindex, nofollow' }
    ]
})

const txId = route.params?.txId

const title = ref<string>()
// The redirect assistant's message outlives this page in an SPA session, so take it once: left in the shared state it
// shows on the next deposit's pending page
const handedOverMessage = useState<string | null>('redirectAssistant:errorMessage')
const message = ref(handedOverMessage.value)
handedOverMessage.value = null
const fetchWithdrawReceipt = ref(false)

const isDeposit = computed(() => !!pspMethod.value && pspMethod.value.toLowerCase() === PspMethodEnum.DEPOSIT)

const isWithdraw = computed(() => !!pspMethod.value && [PspMethodEnum.WITHDRAW, PspMethodEnum.WITHDRAWAL].includes(pspMethod.value.toLowerCase() as PspMethodEnum))

const { data: receiptData, error: receiptError } = useReceipt(
    route.params.txId as string,
    route.query.psp as PspEnum,
    { enabled: () => isWithdraw.value && !!route.params.txId && route.params.txId !== 'null' && !!route.query.psp && !!fetchWithdrawReceipt.value }
)

const redirectPageUrl = computed(() => {
    if (isWithdraw.value) {
        return pendingPageRedirectUrl?.withdraw ?? '/users/withdraw'
    }

    return pendingPageRedirectUrl?.deposit ?? t('pages.lobby')
})

function generateRedirectUrlParams(): LocationQueryRaw {
    const paramsObject = {
        txId,
        brand: site.brand,
        psp: route.query.psp,
    }

    return paramsObject
}

// Regenerated client returns states UPPER_CASE; usePayments helpers normalise, so accept either casing.
type TransactionStateInput = TransactionStateUnion | Uppercase<TransactionStateUnion>

async function handleRedirect(state?: TransactionStateInput) {
    if (isReturning.value) return

    isReturning.value = true

    try {
        await performRedirect(state)
    }
    catch {
        await navigateTo(t('pages.lobby'), { external: true })
    }
}

async function performRedirect(state?: TransactionStateInput) {
    if (!route.query.transactionType) {
        await navigateTo(pendingPageRedirectUrl?.deposit ?? t('pages.lobby'))

        return
    }

    if (isWithdraw.value) {
        await navigateTo(t('pages.withdraw'))

        return
    }

    const redirectParams = generateRedirectUrlParams()
    const gameSlug = route.query?.gameSlug as string
    const gameInstanceId = route.query.gameInstanceId as string || null
    const path = getGameUrl()
    const gameParams = getGameParams({
        gameSlug,
        gameInstanceId,
    })
    // A deposit that never left the site still runs the build the player started on, so playgame is
    // re-entered as a full page load; a page the PSP sent the player back to is current already.
    const external = !loadedOnPaymentsPage()
    const returnToPlaygame = async (query: LocationQueryRaw) => {
        if (external) markDepositReturnFullLoad()
        await navigateTo({ path, query }, { external })
    }

    if (state && typeof state === 'string' && isTransactionSuccessful(state)) {
        if (route.query?.gameSlug) {
            await returnToPlaygame({
                ...redirectParams,
                ...gameParams,
            })

            return
        }

        if (['true', '1'].includes(route.query?.isPlaygame as string)) {
            sendMessage({
                type: BroadcastMessageType.PLAYGAME_DEPOSIT_SUCCESSFUL,
                data: redirectParams
            })

            // No game to restart: return to playgame (game selector + receipt via txId) rather than closing the window.
            await returnToPlaygame({
                ...redirectParams,
                new: '1',
            })

            return
        }

        await navigateTo({
            path: redirectPageUrl.value,
            query: redirectParams,
        })

        return
    }

    if (redirectParams?.txId) {
        delete redirectParams.txId
    }

    if (route.query?.gameSlug) {
        await returnToPlaygame({
            ...redirectParams,
            ...gameParams,
        })

        return
    }

    if (['true', '1'].includes(route.query?.isPlaygame as string)) {
        await returnToPlaygame({
            ...redirectParams,
            new: '1',
        })

        return
    }

    await navigateTo(t('pages.deposit'))
}

type TransactionStatus = Awaited<ReturnType<typeof waitForTransactionStatus>>

const statusPolling = new AbortController()

onBeforeUnmount(() => statusPolling.abort())

async function checkDepositStatus() {
    const { status, error } = await waitForTransactionStatus(txId as string, route.query.psp as PspEnum, { signal: statusPolling.signal }).then(
        status => ({ status, error: undefined }),
        (error: unknown) => ({ status: undefined, error }),
    )

    // Left the page while polling: navigating now would take the player away from the page they went to
    if (statusPolling.signal.aborted) return

    await handleDepositStatus(status, error)
}

async function handleDepositStatus(status: TransactionStatus, statusError: unknown) {
    if (statusError || isTransactionFailed(status?.state) || isTransactionInconsistent(status?.state)) {
        title.value = t('payments.deposit.pending.failure.title')

        // A failed request shows the default message, not its error body: the status query used to wrap the request
        // error, so the body never reached players
        message.value = status?.message ?? t('core.defaultErrorMessage')

        if (isDeposit.value) {
            if (isUsersFirstDeposit.value) {
                await trackEvent({
                    event_name: 'signup_deposit_endpoint_error_transaction_status',
                    event_type: 'Load',
                    meta_data: [
                        {
                            category: 'Deposit',
                            errorMessage: message.value,
                        },
                    ],
                })
            }
        }

        return
    }

    if (isTransactionSuccessful(status?.state)) {
        handleRedirect(status?.state)

        return
    }

    // Still pending after the last poll
    title.value = t(`payments.${pspMethod.value}.pending.failure.receipt.title`)
    message.value = t(`payments.${pspMethod.value}.pending.failure.receipt.message`)

    if (isDeposit.value) {
        if (isUsersFirstDeposit.value) {
            await trackEvent({
                event_name: 'signup_deposit_endpoint_error_exceeded_max_retries_for_transaction_status',
                event_type: 'Load',
                meta_data: [
                    { category: 'Deposit' }
                ]
            })
        }
    }
}

async function handleOpenLoopWithdrawalStatus() {
    if (isTransactionFailed(receiptData.value?.state) || receiptError.value) {
        title.value = t('payments.withdraw.pending.failure.title')

        // A failed withdrawal surfaces its message through the request error, not the success receipt.
        const error = receiptError.value
        const errorBody = OpenapiError.isError(error) ? error.data : undefined
        const firstError = errorBody?.errors?.[0]
        const firstErrorMessage = typeof firstError === 'string' ? firstError : firstError?.msg

        message.value = firstErrorMessage ?? errorBody?.error ?? errorBody?.message ?? error?.message ?? t('core.defaultErrorMessage')

        return
    }

    await navigateTo({
        path: '/users/withdraw',
        // Receipt is coerced into query params (existing behaviour); shape doesn't match LocationQueryRaw.
        query: receiptData.value?.receipt as unknown as LocationQueryRaw,
    })
}

if (import.meta.client) {
    whenever(() => receiptData.value || receiptError.value, () => {
        handleOpenLoopWithdrawalStatus()
    }, { immediate: true })
}

onMounted(async () => {
    // The redirect assistant gets here without a full page load, so the cashier's deposit is still in flight; left set,
    // it holds back build reloads and playgame's lobby redirects. Not ended earlier: until playgame has unmounted, its
    // payments toggles read requestDataObject as the deposit taking the player away
    resetPayment()

    if (!isLoggedIn.value) {
        await navigateTo(pendingPageRedirectUrl?.deposit ?? t('pages.lobby'))

        return
    }

    if (!route.params.txId) {
        await navigateTo(t('pages.lobby'))

        return
    }

    // User has seen their message already, so redirect to frontpage
    if (route.params.txId === 'null' && !message.value) {
        await navigateTo(pendingPageRedirectUrl?.deposit ?? t('pages.lobby'))

        return
    }

    if (!route.params.txId || message.value) {
        title.value = t('payments.deposit.pending.failure.title')
    }

    const transactionType = (route.query?.transactionType as string)?.toLowerCase() as PspMethodUnion || PspMethodEnum.DEPOSIT

    pspMethod.value = transactionType === PspMethodEnum.WITHDRAWAL
        ? PspMethodEnum.WITHDRAW
        : transactionType

    if (isWithdraw.value) {
        startOpenLoopTimeout()

        return
    }

    if (!isDeposit.value || route.params.txId === 'null' || !route.query.psp) return

    await checkDepositStatus()
})
</script>

<template>
    <UIApp>
        <div class="pending" data-test="pending">
            <div v-if="pspMethod" class="pending__content">
                <div>
                    <NuxtImg
                        v-if="logo?.src"
                        preset="local"
                        loading="lazy"
                        provider="cloudflare"
                        decoding="async"
                        :src="logo.src"
                        :quality="logo.quality"
                        height="40"
                        alt="Site logo"
                        class="pending__logo"
                    />
                </div>

                <template v-if="!message">
                    <div>
                        <h1 data-test="pending-title" class="pending__title">
                            {{ t(`payments.${pspMethod}.pending.title`) }}
                        </h1>

                        <UILoader
                            data-test="pending-loader"
                            class="pending__loader"
                            :inline="true"
                            :text="t(`payments.${pspMethod}.pending.loadingText`)"
                        />

                        <p data-test="pending-subtitle" class="pending__subtitle">
                            {{ t(`payments.${pspMethod}.pending.subtitle`) }}
                        </p>
                    </div>
                </template>

                <template v-else>
                    <div>
                        <h1 data-test="pending-title" class="pending__title">
                            {{ title }}
                        </h1>

                        <!-- eslint-disable-next-line vue/no-v-html message is safe -->
                        <p data-test="pending-message" class="pending__message" v-html="message" />
                    </div>

                    <div>
                        <UIButton
                            data-test="pending-button"
                            class="pending__button"
                            as="cta"
                            :disabled="isReturning"
                            size="xl"
                            @click="handleRedirect"
                        >
                            <UILoader
                                v-if="isReturning"
                                text=""
                                :color="false"
                                :inline="true"
                                width="40px"
                                height="40px"
                            />

                            <template v-else>
                                {{ t(`payments.${pspMethod}.pending.returnButtonText`) }}
                            </template>
                        </UIButton>
                    </div>
                </template>
            </div>
        </div>
    </UIApp>
</template>

<style lang="scss" scoped>
:deep(.loader--inline) {
    margin-left: 0;
}

.pending {
    text-align: center;
    width: 100%;
    height: 100vh;
    padding: 20px;

    &__content {
        margin-top: 20px;
        display: flex;
        flex-direction: column;
        gap: 20px;
    }

    &__title {
        margin-top: 50px;
        font-size: $font-size-lg;
        font-weight: $text-bold;
    }

    &__subtitle {
        margin-top: 50px;
        font-weight: $text-regular;
        font-size: $font-size-sm;
    }

    &__message {
        font-size: $font-size-base;
    }
}
</style>
